import * as React from "react"
import { cva } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Stack component for vertical layouts with consistent spacing
 */
const stackVariants = cva(
    "flex flex-col",
    {
        variants: {
            spacing: {
                xs: "gap-1",
                sm: "gap-2",
                md: "gap-4",
                lg: "gap-6",
                xl: "gap-8",
                "2xl": "gap-12",
                "3xl": "gap-16",
            },
            align: {
                start: "items-start",
                center: "items-center",
                end: "items-end",
                stretch: "items-stretch",
            },
            justify: {
                start: "justify-start",
                center: "justify-center",
                end: "justify-end",
                between: "justify-between",
                around: "justify-around",
            },
        },
        defaultVariants: {
            spacing: "md",
            align: "stretch",
        },
    }
)

const Stack = React.forwardRef(
    ({ className, spacing, align, justify, ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(stackVariants({ spacing, align, justify, className }))}
                {...props}
            />
        )
    }
)
Stack.displayName = "Stack"

/**
 * Inline component for horizontal layouts with consistent spacing
 */
const inlineVariants = cva(
    "flex flex-row",
    {
        variants: {
            spacing: {
                xs: "gap-1",
                sm: "gap-2",
                md: "gap-4",
                lg: "gap-6",
                xl: "gap-8",
                "2xl": "gap-12",
            },
            align: {
                start: "items-start",
                center: "items-center",
                end: "items-end",
                stretch: "items-stretch",
                baseline: "items-baseline",
            },
            justify: {
                start: "justify-start",
                center: "justify-center",
                end: "justify-end",
                between: "justify-between",
                around: "justify-around",
            },
            wrap: {
                true: "flex-wrap",
                false: "flex-nowrap",
            },
        },
        defaultVariants: {
            spacing: "md",
            align: "center",
            wrap: false,
        },
    }
)

const Inline = React.forwardRef(
    ({ className, spacing, align, justify, wrap, as, ...props }, ref) => {
        const Comp = as || "div"
        return (
            <Comp
                ref={ref}
                className={cn(inlineVariants({ spacing, align, justify, wrap, className }))}
                {...props}
            />
        )
    }
)
Inline.displayName = "Inline"

/**
 * Container component for consistent max-width and padding
 */
const containerVariants = cva(
    "w-full mx-auto px-4 sm:px-6 lg:px-8",
    {
        variants: {
            size: {
                sm: "max-w-screen-sm",
                md: "max-w-screen-md",
                lg: "max-w-screen-lg",
                xl: "max-w-screen-xl",
                "2xl": "max-w-screen-2xl",
                full: "max-w-full",
            },
        },
        defaultVariants: {
            size: "xl",
        },
    }
)

const Container = React.forwardRef(
    ({ className, size, ...props }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(containerVariants({ size, className }))}
                {...props}
            />
        )
    }
)
Container.displayName = "Container"

export { Stack, Inline, Container }
